/**
 * The Driver App's push token (EP10-S21, handed over by EP12-S07).
 *
 * FCM delivers to this PWA's own service worker (`public/push-sw.js`, imported
 * into the generated worker): the Firebase JS SDK's `getToken` subscribes that
 * registration with the VAPID key and returns the FCM registration token, which
 * the sync loop sends as `pushToken` on `POST /devices` and sends again when it
 * changes.
 *
 * **Degrades to nothing.** No Firebase config in the build, no service worker
 * (`vite dev`), a browser FCM does not support, or notifications not granted:
 * the token is undefined, the device registers without one, and the server
 * re-routes the driver's alerts to the inbox (EP12-S07). The acknowledgment
 * prompt never depends on push: it comes from the sync (EP12-S15).
 */

/** The Firebase web app's config, as `VITE_FIREBASE_CONFIG` holds it (JSON). */
export interface FirebaseWebConfig {
	apiKey: string;
	projectId: string;
	messagingSenderId: string;
	appId: string;
}

/** The build variables this reads. Both are public by design, like the WorkOS client id. */
export interface PushEnv {
	VITE_FIREBASE_CONFIG?: string | undefined;
	VITE_FIREBASE_VAPID_KEY?: string | undefined;
}

/** The config and VAPID key, or null when the build has not got both. */
export function firebaseSettings(
	env: PushEnv,
): { config: FirebaseWebConfig; vapidKey: string } | null {
	const vapidKey = env.VITE_FIREBASE_VAPID_KEY?.trim();
	if (!env.VITE_FIREBASE_CONFIG || !vapidKey) {
		return null;
	}
	try {
		const config = JSON.parse(env.VITE_FIREBASE_CONFIG) as Partial<FirebaseWebConfig>;
		const { apiKey, projectId, messagingSenderId, appId } = config;
		if (!apiKey || !projectId || !messagingSenderId || !appId) {
			return null;
		}
		return { config: { apiKey, projectId, messagingSenderId, appId }, vapidKey };
	} catch {
		return null;
	}
}

/** Where the phone stands on notifications, for Profile. */
export type PushState = "unavailable" | "off" | "blocked" | "on";

/** Whether push can work here, and whether the driver allowed it. */
export function pushState(env: PushEnv): PushState {
	if (
		firebaseSettings(env) === null ||
		typeof Notification === "undefined" ||
		!("serviceWorker" in navigator)
	) {
		return "unavailable";
	}
	switch (Notification.permission) {
		case "granted":
			return "on";
		case "denied":
			return "blocked";
		default:
			return "off";
	}
}

/**
 * The FCM registration token, or undefined whenever push cannot work. Never
 * asks for permission: that is Profile's button, a tap the browser requires.
 */
export async function pushToken(env: PushEnv): Promise<string | undefined> {
	const settings = firebaseSettings(env);
	if (settings === null || pushState(env) !== "on") {
		return undefined;
	}
	try {
		// Not `ready`: that never resolves where no worker is registered.
		const registration = await navigator.serviceWorker.getRegistration();
		if (registration === undefined) {
			return undefined;
		}
		const { getApps, initializeApp } = await import("firebase/app");
		const { getMessaging, getToken, isSupported } = await import("firebase/messaging");
		if (!(await isSupported())) {
			return undefined;
		}
		const app = getApps()[0] ?? initializeApp(settings.config);
		return (
			(await getToken(getMessaging(app), {
				vapidKey: settings.vapidKey,
				serviceWorkerRegistration: registration,
			})) || undefined
		);
	} catch {
		// FCM unreachable or refused: the next sync tries again.
		return undefined;
	}
}

/** The messages the service worker posts to the open app (`public/push-sw.js`). */
export type WorkerMessage = { type: "khazzin.push" } | { type: "khazzin.open"; link: string };

/** A deep link the app may follow: a path on this origin, nothing else (`//` and `/\` leave it). */
export function safeLink(link: unknown): string {
	return typeof link === "string" && /^\/(?![/\\])/.test(link) ? link : "/today";
}
